/**
 * CR042: Admin Layout with sidebar navigation
 * CR111C: Added canary marker and visible empty state
 * CR113: Accordion-based navigation with section grouping
 * CR283: Added pending merchant count badge
 * CR318: Added production health badges and drawer
 */

import { useEffect, useState } from "react";
import { useResizableSidebar } from "@/hooks/useResizableSidebar";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Home, ChevronLeft } from "lucide-react";
import {
  adminNavSections,
  getActiveSectionKey,
  isNavItemActive,
} from "./adminNavConfig";
import { useMerchantsPendingCount } from "@/hooks/useMerchantsDirectory";
import { ProductionHealthProvider, useProductionHealth } from "@/hooks/useProductionHealth";
import { NavHealthBadge, NavHealthDot } from "./NavHealthBadge";
import { ProductionHealthDrawer } from "./ProductionHealthDrawer";

// CR113: Updated canary version
const ADMIN_LAYOUT_CANARY = "CR113-v1";

// Storage key for accordion state
const ACCORDION_STATE_KEY = "admin-nav-accordion-state";

interface AdminLayoutProps {
  children: React.ReactNode;
}

// Inner layout that uses the health context
function AdminLayoutInner({ children }: AdminLayoutProps) {
  const location = useLocation();
  const health = useProductionHealth();
  const { width: sidebarWidth, layoutRef, startDrag, resetWidth } = useResizableSidebar();
  const [isDesktop, setIsDesktop] = useState(false);

  // CR565: Track desktop breakpoint for resize handle visibility
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    setIsDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  
  // CR283: Fetch pending merchant count for nav badge
  const { data: pendingCount = 0 } = useMerchantsPendingCount();

  // CR113: Initialize with all sections expanded, then hydrate from localStorage
  const [expandedSections, setExpandedSections] = useState<string[]>(() => {
    // Default: all sections expanded
    return adminNavSections.map((s) => s.sectionKey);
  });

  // CR113: Hydrate from localStorage after mount (SSR-safe)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(ACCORDION_STATE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setExpandedSections(parsed);
        }
      }
    } catch (e) {
      console.warn("[AdminLayout] Failed to parse accordion state:", e);
    }
  }, []);

  // CR113: Persist accordion state changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(ACCORDION_STATE_KEY, JSON.stringify(expandedSections));
    } catch (e) {
      console.warn("[AdminLayout] Failed to save accordion state:", e);
    }
  }, [expandedSections]);

  // CR113: Ensure active section is expanded when route changes
  useEffect(() => {
    const activeKey = getActiveSectionKey(location.pathname);
    if (activeKey && !expandedSections.includes(activeKey)) {
      setExpandedSections((prev) => [...prev, activeKey]);
    }
  }, [location.pathname]);

  // CR111C: Log mount for debugging
  useEffect(() => {
    console.log(
      `[AdminLayout] ${ADMIN_LAYOUT_CANARY}: AdminLayout mounted at ${location.pathname}`
    );
  }, [location.pathname]);

  // CR111C: Defensive check - if children is null/undefined, show visible empty state
  if (!children) {
    return (
      <div className="flex min-h-screen bg-background">
        <aside className="w-64 border-r bg-muted/30 flex flex-col">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Admin</h2>
              <Button variant="ghost" size="icon" asChild>
                <Link to="/">
                  <Home className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </aside>
        <main className="flex-1 overflow-auto">
          <div className="container max-w-6xl py-8">
            <div className="text-xs text-muted-foreground/50 font-mono mb-2">
              [Canary: {ADMIN_LAYOUT_CANARY}] AdminLayout: no children provided
            </div>
            <Skeleton className="h-40 w-full" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div ref={layoutRef} className="flex min-h-screen bg-background">
      {/* CR113: Canary marker (fixed position, subtle) */}
      <div className="fixed bottom-1 left-1 text-[10px] text-muted-foreground/30 font-mono pointer-events-none z-50">
        {ADMIN_LAYOUT_CANARY}
      </div>

      {/* CR318: Production Health Drawer */}
      <ProductionHealthDrawer />

      {/* CR565: Sidebar with dynamic width on desktop */}
      <aside
        className="border-r bg-muted/30 flex flex-col shrink-0"
        style={isDesktop ? { width: sidebarWidth } : { width: '16rem' }}
      >
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Admin</h2>
            <Button variant="ghost" size="icon" asChild>
              <Link to="/">
                <Home className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
        <ScrollArea className="flex-1">
          <nav className="p-2">
            <Accordion
              type="multiple"
              value={expandedSections}
              onValueChange={setExpandedSections}
              className="space-y-1"
            >
              {adminNavSections.map((section) => (
                <AccordionItem
                  key={section.sectionKey}
                  value={section.sectionKey}
                  className="border-none"
                >
                  <AccordionTrigger
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
                      "hover:bg-muted hover:no-underline transition-colors",
                      "[&[data-state=open]>svg:last-child]:rotate-180"
                    )}
                  >
                    <section.sectionIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 text-left">{section.sectionLabel}</span>
                    {section.sectionKey === "runs" && (
                      <NavHealthBadge 
                        onClick={health.openDrawer} 
                        showLabel={false}
                        className="mr-2"
                      />
                    )}
                    {section.sectionKey === "merchants" && pendingCount > 0 && (
                      <Badge variant="secondary" className="ml-auto mr-2 text-xs">
                        {pendingCount}
                      </Badge>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="pb-1 pt-0">
                    <div className="ml-4 space-y-1 border-l pl-3">
                      {section.items.map((item) => {
                        const isActive = isNavItemActive(
                          location.pathname,
                          location.search,
                          item.href
                        );
                        const isLivePreview = item.href === "/admin/live-preview";
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            className={cn(
                              "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                              isActive
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                          >
                            <item.icon className="h-3.5 w-3.5" />
                            <span className="flex-1">{item.title}</span>
                            {isLivePreview && !isActive && (
                              <NavHealthDot 
                                onClick={() => health.openDrawer()}
                              />
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </nav>
        </ScrollArea>
        <div className="p-4 border-t">
          <Button variant="outline" className="w-full" asChild>
            <Link to="/">
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </aside>

      {/* CR565: Drag resize handle (desktop only) */}
      {isDesktop && (
        <div
          className="w-1 cursor-col-resize bg-transparent hover:bg-primary/20 active:bg-primary/40 transition-colors shrink-0 relative group"
          onMouseDown={startDrag}
          onDoubleClick={resetWidth}
          title="Drag to resize sidebar · Double-click to reset"
        >
          <div className="absolute inset-y-0 -left-0.5 -right-0.5 group-hover:bg-primary/10" />
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto min-w-0">
        <div className="container max-w-6xl py-8">{children}</div>
      </main>
    </div>
  );
}

// CR318: Wrap with ProductionHealthProvider and TooltipProvider
export function AdminLayout({ children }: AdminLayoutProps) {
  return (
    <ProductionHealthProvider>
      <TooltipProvider>
        <AdminLayoutInner>{children}</AdminLayoutInner>
      </TooltipProvider>
    </ProductionHealthProvider>
  );
}
